import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

import { useCatalogLabels } from '../labels';

const STORAGE_KEY = 'pih.treeWidth';
/** Tailwind's w-72 - the width the sidebar had before it became resizable. */
const DEFAULT_WIDTH = 288;
const MIN_WIDTH = 220;
const MAX_WIDTH = 640;
const KEY_STEP = 16;

const clamp = (width: number): number => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));

const readWidth = (): number => {
    try {
        const stored = Number(window.localStorage.getItem(STORAGE_KEY));
        return Number.isFinite(stored) && stored > 0 ? clamp(stored) : DEFAULT_WIDTH;
    } catch {
        return DEFAULT_WIDTH;
    }
};

const storeWidth = (width: number): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
        // Storage blocked (private window) - the width then only holds until the page reloads.
    }
};

/**
 * The catalog's tree sidebar, made wider or narrower by dragging its right edge (see chat: long
 * names like "Aluminiumschalungsträger" were cut to "Aluminiumschalu…").
 *
 * The width is remembered in this browser (localStorage) and shared by the product and the
 * document view. Double-click on the edge, or Home on the focused handle, returns to the original
 * 288 px; the arrow keys move it in 16 px steps. Only from `md` up - below that the tree lives in a
 * drawer, as before.
 *
 * Dragging re-renders only this wrapper: `children` is the same element object on every pass, so
 * React does not re-render the tree inside it.
 */
export const ResizableAside = ({ children }: { children: ReactNode }) => {
    const { L } = useCatalogLabels();
    const [width, setWidth] = useState(readWidth);
    const drag = useRef<{ startX: number; startWidth: number } | null>(null);

    const commit = useCallback((next: number) => {
        const clamped = clamp(next);
        setWidth(clamped);
        storeWidth(clamped);
    }, []);

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        drag.current = { startX: event.clientX, startWidth: width };
        event.currentTarget.setPointerCapture(event.pointerId);
        // No text selection across the page while dragging.
        document.body.style.userSelect = 'none';
    };

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (drag.current) {
            setWidth(clamp(drag.current.startWidth + event.clientX - drag.current.startX));
        }
    };

    const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (!drag.current) {
            return;
        }
        commit(drag.current.startWidth + event.clientX - drag.current.startX);
        drag.current = null;
        document.body.style.userSelect = '';
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            commit(width + (event.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP));
        } else if (event.key === 'Home') {
            event.preventDefault();
            commit(DEFAULT_WIDTH);
        }
    };

    return (
        <aside className="relative hidden md:block md:shrink-0" style={{ width }}>
            {children}
            <div
                aria-label={L.resizeTree}
                aria-orientation="vertical"
                aria-valuemax={MAX_WIDTH}
                aria-valuemin={MIN_WIDTH}
                aria-valuenow={width}
                className="pih-aside-resizer"
                role="separator"
                tabIndex={0}
                title={L.resizeTree}
                onDoubleClick={() => commit(DEFAULT_WIDTH)}
                onKeyDown={handleKeyDown}
                onPointerCancel={handlePointerUp}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            />
        </aside>
    );
};
